import { useCallback, useEffect, type ReactNode } from 'react'
import { useAuth } from 'react-oidc-context'
import AuthLoading from './AuthLoading'

/**
 * Auth-guard (FR-014). Oturum yoksa `signinRedirect` tetikler; giriş sonrası kaldığı ekrana dönebilmek
 * için istenen rota `state.returnTo` ile taşınır (FR-015).
 *
 * <p><b>Flash önleme:</b> Kimlik belirsizken korumalı içerik render edilmez, nötr yükleme gösterilir.
 * <p><b>Çıkmaz önleme:</b> Bir auth hatasında ekran kilitlenmez — kullanıcıya "Tekrar dene" (yeniden
 * giriş) sunulur. Giriş için PKCE {@code code_challenge} → {@code crypto.subtle.digest} gerekir;
 * güvensiz bağlamda (HTTP + IP) native {@code crypto.subtle} yoktur ama {@code cryptoSubtlePolyfill}
 * onu saf-JS SHA-256 ile sağlar. Polyfill de kurulamadıysa giriş fiilen imkânsızdır → otomatik
 * yönlendirme yapılmaz, çıkmaz yerine "HTTPS gerekli" uyarısı gösterilir.
 */
export default function RequireAuth({ children }: Readonly<{ children: ReactNode }>) {
  const auth = useAuth()
  // Giriş, PKCE için `crypto.subtle.digest`'e bağlıdır. `isSecureContext`'e değil, gerçekten gereken
  // yeteneğe bak: polyfill güvensiz bağlamda subtle'ı sağladıysa giriş mümkündür (engelleme). Ancak
  // subtle hâlâ yoksa (polyfill kurulamadı) giriş başlatılamaz → uyarı. Test ortamı subtle sağlar.
  const insecure = typeof window !== 'undefined' && !window.crypto?.subtle

  const startLogin = useCallback(() => {
    auth.signinRedirect({
      state: { returnTo: window.location.pathname + window.location.search },
    })
  }, [auth])

  useEffect(() => {
    // Otomatik giriş yalnız temiz + güvenli durumda: hata yok, güvenli bağlam, yükleme/navigator yok.
    // Hata/güvensiz durumda otomatik başlatma yapılmaz (doomed akış sonsuz döngü/çıkmaz üretmesin).
    if (
      !auth.isLoading &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.error &&
      !insecure
    ) {
      startLogin()
    }
  }, [
    auth.isLoading,
    auth.isAuthenticated,
    auth.activeNavigator,
    auth.error,
    insecure,
    startLogin,
  ])

  const retry = useCallback(() => {
    auth.clearStaleState?.()
    startLogin()
  }, [auth, startLogin])

  // Oturum açık → uygulamayı göster (güvensiz-bağlam uyarısı yalnız girişte anlamlı; girişli kullanıcıyı
  // engelleme — test/normal akış bundan etkilenmez).
  if (auth.isAuthenticated) {
    return <>{children}</>
  }

  // Oturumsuz + hata/güvensiz bağlam → çıkmaz yerine recovery ekranı.
  if (auth.error || insecure) {
    return <AuthLoading error={auth.error?.message} onRetry={retry} insecure={insecure} />
  }

  // Belirsiz / yönlendirme sürüyor → nötr yükleme (flash önleme).
  return <AuthLoading />
}
